import React, { useState } from 'react';
import MetricExplorer from './MetricExplorer';
import InvoiceDetail from './InvoiceDetail';

/*
 * FinanceDrilldown — the Revenue & receivables drill-downs (Card P150).
 *
 * ⭐ A CONFIGURATION, NOT A COMPONENT. It hands MetricExplorer the finance
 * channel prefix and the finance column allowlist, and that is all. The
 * summary-first flow, the 25-row paging, the sort, the cap disclosure, the
 * export refusal handling and the "failed ≠ empty" states are #722's, unchanged
 * — which is what makes this match the rest of the dashboard rather than
 * resemble it.
 */

/**
 * ⚠⚠ THE INVOICE ROW ALLOWLIST. A field the server should never have sent is
 * NOT rendered even if it arrives — the same invariant #722's opportunity
 * columns carry, applied to a different shape.
 *
 * ⛔ `bill_to` is the ONLY identifying field here, and it reaches the client
 * already resolved: the server prefers the CRM contact's name over the
 * invoice's encrypted snapshot, and deletes the ciphertext before responding.
 * There is no `bill_to_name_encrypted` to render, by construction.
 */
export const INVOICE_COLUMNS = Object.freeze([
  { key: 'invoice_number', label: 'Invoice #', sortAs: 'text', mono: true },
  { key: 'bill_to', label: 'Billed to', sortAs: 'text' },
  { key: 'issued_at', label: 'Issued', sortAs: 'date' },
  { key: 'due_at', label: 'Due', sortAs: 'date' },
  { key: 'days_overdue', label: 'Days over', sortAs: 'number', align: 'right' },
  // ⚠ cents: true — these are integer cents, and the formatter is told so.
  // The 28 Oct bug was a cents value handed to a dollars formatter.
  { key: 'total_cents', label: 'Total', sortAs: 'number', money: true, cents: true, align: 'right' },
  { key: 'paid_cents', label: 'Paid', sortAs: 'number', money: true, cents: true, align: 'right' },
  { key: 'balance_cents', label: 'Balance', sortAs: 'number', money: true, cents: true, align: 'right' },
]);

export const PAYMENT_COLUMNS = Object.freeze([
  { key: 'transacted_at', label: 'Date', sortAs: 'date' },
  { key: 'contact_name', label: 'Payer', sortAs: 'lastName' },
  { key: 'amount_cents', label: 'Amount', sortAs: 'number', money: true, cents: true, align: 'right' },
  { key: 'provider', label: 'Provider', sortAs: 'text' },
  { key: 'ghl_invoice_id', label: 'Invoice #', sortAs: 'text', mono: true },
  { key: 'status', label: 'Status', sortAs: 'text' },
]);

/** Which column set a metric's rows use. Code-supplied, never derived from data. */
const COLUMNS_FOR = Object.freeze({
  'finance.overdue': INVOICE_COLUMNS,
  'finance.invoiced': INVOICE_COLUMNS,
  'finance.deposits': INVOICE_COLUMNS,
  'finance.collected': PAYMENT_COLUMNS,
  'finance.refunded': PAYMENT_COLUMNS,
});

/**
 * ⚠ LITERAL CHANNEL NAMES. See the note in MetricSummary: the P123 caller
 * ratchet finds a channel's caller by its literal name in client source, so a
 * built string reads as callerless and disarms the guard.
 */
export const FINANCE_CHANNELS = Object.freeze({
  summary: 'finance:drilldown:summary',
  resolve: 'finance:drilldown:resolve',
  export: 'finance:drilldown:export',
});

export default function FinanceDrilldown({ metricKey, title, onClose, onOpenContact, canExport }) {
  // Which invoice row is open, if any. An invoice list row opens the detail;
  // a payment list row does not (it has no invoice of its own to show).
  const [invoiceId, setInvoiceId] = useState(null);
  const columns = COLUMNS_FOR[metricKey];
  /*
   * ⚠ An unknown metric renders NOTHING rather than falling back to the
   * opportunity columns. A fallback would render finance rows through a shape
   * nobody reviewed, and the mismatch would show as blank cells rather than as
   * an error — which is how a wrong column set survives a demo.
   */
  if (!columns) return null;

  if (invoiceId != null) {
    return (
      <InvoiceDetail
        invoiceId={invoiceId}
        onClose={() => setInvoiceId(null)}
        // ⚠ On an INVOICE list a row opens the invoice, not the contact — the
      // invoice is the record the figure is made of, and the contact is one
      // click further on. On a PAYMENT list there is no invoice row to open, so
      // the contact hand-off is kept.
      onOpenContact={columns === INVOICE_COLUMNS
        ? ((id, row) => setInvoiceId(row && row.id != null ? row.id : null))
        : onOpenContact}
      />
    );
  }

  return (
    <MetricExplorer
      metricKey={metricKey}
      title={title}
      onClose={onClose}
      // ⚠ On an INVOICE list a row opens the invoice, not the contact — the
      // invoice is the record the figure is made of, and the contact is one
      // click further on. On a PAYMENT list there is no invoice row to open, so
      // the contact hand-off is kept.
      onOpenContact={columns === INVOICE_COLUMNS
        ? ((id, row) => setInvoiceId(row && row.id != null ? row.id : null))
        : onOpenContact}
      canExport={canExport}
      channels={FINANCE_CHANNELS}
      columns={columns}
    />
  );
}
